import { Component } from '@theme/component';

/**
 * Product thumbnail strip on Shop the scene tiles and collection cards.
 *
 * @typedef {Object} ShopTheSceneProductStripRefs
 * @property {HTMLButtonElement} toggle - Plus button
 * @property {HTMLElement} panel - Thumbnail strip panel
 */

/**
 * @extends {Component<ShopTheSceneProductStripRefs>}
 */
class ShopTheSceneProductStrip extends Component {
  requiredRefs = ['toggle', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.toggle.addEventListener('click', this.#handleToggleClick);
    this.addEventListener('click', this.#handleThumbClick);
    this.#syncToggleAccessibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.toggle.removeEventListener('click', this.#handleToggleClick);
    this.removeEventListener('click', this.#handleThumbClick);
  }

  #syncToggleAccessibility = () => {
    const { toggle } = this.refs;
    const modalId = this.dataset.modalTarget;
    const hiddenLabel = toggle.querySelector('.visually-hidden');

    toggle.setAttribute('aria-haspopup', 'dialog');
    toggle.setAttribute('aria-expanded', 'false');

    if (modalId) {
      toggle.setAttribute('aria-controls', modalId);
    }

    if (hiddenLabel instanceof HTMLElement) {
      hiddenLabel.textContent = 'Shop products in this scene';
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#openModal();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleThumbClick = (event) => {
    const thumbLink = event.target instanceof Element ? event.target.closest('.shop-the-scene-tile__product-thumb') : null;
    if (!thumbLink) return;

    event.preventDefault();
    event.stopPropagation();
    this.#openModal();
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getModal() {
    const modalId = this.dataset.modalTarget;
    if (!modalId) return null;

    const modal =
      document.querySelector(`shop-the-scene-modal[data-modal-id="${modalId}"]`) ??
      document.getElementById(modalId)?.closest('shop-the-scene-modal');

    return modal instanceof HTMLElement ? modal : null;
  }

  #openModal() {
    const modal = this.#getModal();

    if (modal && 'open' in modal && typeof modal.open === 'function') {
      this.refs.toggle.setAttribute('aria-expanded', 'true');
      modal.open();
    }
  }
}

if (!customElements.get('shop-the-scene-product-strip')) {
  customElements.define('shop-the-scene-product-strip', ShopTheSceneProductStrip);
}
